import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { adminFetch } from '../../lib/adminFetch';
import { API_BASE } from '../../lib/apiBase';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Search,
  Phone,
  Calendar as CalendarIcon,
  Clock,
  MessageCircle,
  User,
  Stethoscope,
  ClipboardList,
  Plus,
} from 'lucide-react';
import { toast } from 'sonner';

// === Status meta ===
const STATUSES = [
  { value: 'new_request', label: 'New Request', cls: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  { value: 'contacted', label: 'Contacted', cls: 'bg-sky-100 text-sky-800 border-sky-200' },
  { value: 'awaiting_response', label: 'Awaiting Response', cls: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  { value: 'confirmed', label: 'Confirmed', cls: 'bg-green-100 text-green-800 border-green-200' },
  { value: 'rescheduled', label: 'Rescheduled', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  { value: 'cancelled', label: 'Cancelled', cls: 'bg-red-100 text-red-800 border-red-200' },
  { value: 'completed', label: 'Completed', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  { value: 'no_show', label: 'No Show', cls: 'bg-gray-200 text-gray-800 border-gray-300' },
];

const STATUS_MAP = STATUSES.reduce((m, s) => ({ ...m, [s.value]: s }), {});

const COMM_KINDS = [
  { value: 'call', label: 'Called patient' },
  { value: 'whatsapp_sent', label: 'WhatsApp message sent' },
  { value: 'patient_confirmed', label: 'Patient confirmed' },
  { value: 'reschedule_requested', label: 'Patient requested reschedule' },
  { value: 'patient_unavailable', label: 'Patient unavailable' },
  { value: 'note', label: 'Other note' },
];

const COMM_KIND_LABEL = COMM_KINDS.reduce((m, k) => ({ ...m, [k.value]: k.label }), {});

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function buildWhatsAppLink(phone, message) {
  const digits = (phone || '').replace(/[^\d]/g, '');
  // Ensure country code; default to India (91) if 10-digit local number
  const e164 = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${e164}?text=${encodeURIComponent(message)}`;
}

export const AdminAppointments = () => {
  const BACKEND_URL = API_BASE;

  const [appointments, setAppointments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');

  // Drawer / dialogs
  const [activeId, setActiveId] = useState(null);
  const [waOpen, setWaOpen] = useState(false);
  const [waMessage, setWaMessage] = useState('');
  const [waPhone, setWaPhone] = useState('');

  const activeAppt = useMemo(
    () => appointments.find((a) => a.id === activeId) || null,
    [appointments, activeId],
  );

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.set('status_filter', statusFilter);
      if (search.trim()) params.set('search', search.trim());
      if (departmentFilter && departmentFilter !== 'all') params.set('department', departmentFilter);
      if (dateFilter) params.set('date', dateFilter);
      const url = `${BACKEND_URL}/api/admin/appointments?${params.toString()}`;
      const res = await adminFetch(url, { credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setAppointments(data);
    } catch (e) {
      console.error(e);
      toast.error(`Failed to load appointments: ${e.message || 'unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL, statusFilter, search, departmentFilter, dateFilter]);

  // Fetch metadata once
  useEffect(() => {
    (async () => {
      try {
        const [d, dept] = await Promise.all([
          fetch(`${BACKEND_URL}/api/doctors/synced`).then((r) => (r.ok ? r.json() : [])),
          fetch(`${BACKEND_URL}/api/departments`).then((r) => (r.ok ? r.json() : [])),
        ]);
        setDoctors(d);
        setDepartments(dept);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [BACKEND_URL]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  // ===== Mutations =====
  const updateStatus = async (id, newStatus, note) => {
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/appointments/${id}/status`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, note: note || null }),
      });
      if (!res.ok) throw new Error('failed');
      const updated = await res.json();
      setAppointments((prev) => prev.map((a) => (a.id === id ? updated : a)));
      toast.success(`Marked as ${STATUS_MAP[newStatus]?.label || newStatus}`);
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const updateAdminFields = async (id, fields) => {
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/appointments/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'failed');
      }
      const updated = await res.json();
      setAppointments((prev) => prev.map((a) => (a.id === id ? updated : a)));
      toast.success('Updated');
    } catch (e) {
      toast.error(`Update failed: ${e.message}`);
    }
  };

  const addCommLog = async (id, kind, note) => {
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/appointments/${id}/comm-log`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind, note: note || null }),
      });
      if (!res.ok) throw new Error('failed');
      const updated = await res.json();
      setAppointments((prev) => prev.map((a) => (a.id === id ? updated : a)));
      toast.success('Communication log added');
    } catch (e) {
      toast.error('Failed to add comm log');
    }
  };

  const updatePatientDetails = async (id, fields) => {
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/appointments/${id}/patient`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'failed');
      }
      const updated = await res.json();
      setAppointments((prev) => prev.map((a) => (a.id === id ? updated : a)));
      toast.success('Patient details updated');
    } catch (e) {
      toast.error(`Update failed: ${e.message}`);
    }
  };

  // ===== WhatsApp builder =====
  const openWhatsApp = (appt, kind = 'follow_up') => {
    const docOrDept = appt.assignedDoctor || appt.doctor || appt.service || 'a Healthwrx doctor';
    let message = '';
    if (kind === 'follow_up') {
      message =
        `Hello ${appt.name}, this is Healthwrx. We received your appointment request for ${docOrDept}. ` +
        `Please confirm your availability for ${appt.date} at ${appt.timeSlot}, ` +
        `or let us know a suitable alternative time.`;
    } else {
      // confirmation
      const cDate = appt.confirmedDate || appt.date;
      const cTime = appt.confirmedTime || appt.timeSlot;
      const dr = appt.assignedDoctor || appt.doctor || appt.service;
      message =
        `Hello ${appt.name}, your appointment at Healthwrx is confirmed.\n\n` +
        `Doctor/Department: ${dr}\n` +
        `Date: ${cDate}\n` +
        `Time: ${cTime}\n` +
        `Location: Healthwrx, Guwahati\n\n` +
        `Please arrive 10 minutes before your appointment time. Thank you.`;
    }
    setWaMessage(message);
    setWaPhone(appt.phone);
    setActiveId(appt.id);
    setWaOpen(true);
  };

  const sendWhatsAppNow = () => {
    if (!waPhone) return;
    window.open(buildWhatsAppLink(waPhone, waMessage), '_blank', 'noopener');
    if (activeId) addCommLog(activeId, 'whatsapp_sent', waMessage.slice(0, 200));
    setWaOpen(false);
  };

  // ===== UI =====
  return (
    <AdminLayout>
      <div className="max-w-7xl font-[Montserrat]">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Appointments</h1>
            <p className="text-sm text-gray-500 mt-1">
              Patient requests come in here. Confirm them manually via WhatsApp or phone.
            </p>
          </div>
          <Button onClick={fetchAppointments} variant="outline" data-testid="appt-refresh-btn">
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-sm mb-6">
          <CardContent className="p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by name or phone…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                    data-testid="appt-search-input"
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="appt-status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger data-testid="appt-department-filter">
                  <SelectValue placeholder="Department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All departments</SelectItem>
                  {departments.map((d) => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                data-testid="appt-date-filter"
              />
            </div>
          </CardContent>
        </Card>

        {/* List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-[#14D1A2] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading appointments…</p>
          </div>
        ) : appointments.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-12 text-center">
              <p className="text-gray-600">No appointments match your filters.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {appointments.map((a) => {
              const meta = STATUS_MAP[a.status] || STATUSES[0];
              return (
                <Card
                  key={a.id}
                  className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setActiveId(a.id)}
                  data-testid={`appt-row-${a.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <h3 className="text-base font-semibold text-gray-900 truncate">{a.name}</h3>
                          <Badge variant="outline" className={`${meta.cls} font-medium`}>
                            {meta.label}
                          </Badge>
                          {a.assignedDoctor && (
                            <Badge variant="outline" className="bg-teal-50 text-teal-800 border-teal-200">
                              Dr: {a.assignedDoctor}
                            </Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-sm text-gray-600">
                          <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{a.phone}</span>
                          <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" />{a.age ? `${a.age} yrs` : '—'} · {a.gender || '—'}</span>
                          <span className="flex items-center gap-1.5"><Stethoscope className="w-3.5 h-3.5" />{a.service || '—'}</span>
                          <span className="flex items-center gap-1.5">
                            <CalendarIcon className="w-3.5 h-3.5" />
                            {a.confirmedDate || a.date} · {a.confirmedTime || a.timeSlot}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-green-600 text-green-700 hover:bg-green-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            openWhatsApp(a, a.status === 'confirmed' ? 'confirmation' : 'follow_up');
                          }}
                          data-testid={`appt-wa-btn-${a.id}`}
                        >
                          <MessageCircle className="w-4 h-4 mr-1" />
                          WhatsApp
                        </Button>
                        <Button
                          size="sm"
                          className="bg-[#14D1A2] hover:bg-[#10B88A]"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveId(a.id);
                          }}
                          data-testid={`appt-open-btn-${a.id}`}
                        >
                          Open
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail drawer */}
      <Sheet open={!!activeId && !waOpen} onOpenChange={(o) => !o && setActiveId(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto font-[Montserrat]">
          {activeAppt && (
            <AppointmentDetail
              appt={activeAppt}
              doctors={doctors}
              departments={departments}
              onUpdateStatus={updateStatus}
              onUpdateFields={updateAdminFields}
              onUpdatePatient={updatePatientDetails}
              onAddCommLog={addCommLog}
              onOpenWhatsApp={openWhatsApp}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* WhatsApp pre-fill dialog */}
      <Dialog open={waOpen} onOpenChange={setWaOpen}>
        <DialogContent className="font-[Montserrat]">
          <DialogHeader>
            <DialogTitle>Message Patient on WhatsApp</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-gray-500">To</Label>
              <Input value={waPhone} onChange={(e) => setWaPhone(e.target.value)} data-testid="wa-phone-input" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Message (editable)</Label>
              <Textarea
                rows={8}
                value={waMessage}
                onChange={(e) => setWaMessage(e.target.value)}
                data-testid="wa-message-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWaOpen(false)}>Cancel</Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={sendWhatsAppNow}
              data-testid="wa-send-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Open WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

// ============================================================
// Detail Panel
// ============================================================
function AppointmentDetail({ appt, doctors, departments, onUpdateStatus, onUpdateFields, onUpdatePatient, onAddCommLog, onOpenWhatsApp }) {
  const meta = STATUS_MAP[appt.status] || STATUSES[0];

  const [assignedDoctor, setAssignedDoctor] = useState(appt.assignedDoctor || '');
  const [confirmedDate, setConfirmedDate] = useState(appt.confirmedDate || appt.date || '');
  const [confirmedTime, setConfirmedTime] = useState(appt.confirmedTime || appt.timeSlot || '');
  const [adminNotes, setAdminNotes] = useState(appt.adminNotes || '');
  const [statusNote, setStatusNote] = useState('');
  const [logKind, setLogKind] = useState('call');
  const [logNote, setLogNote] = useState('');

  // Edit patient details (collapsed by default)
  const [editingPatient, setEditingPatient] = useState(false);
  const [patientForm, setPatientForm] = useState({
    name: appt.name || '',
    phone: appt.phone || '',
    age: appt.age != null ? String(appt.age) : '',
    gender: appt.gender || '',
    healthConcern: appt.healthConcern || '',
    service: appt.service || '',
    doctor: appt.doctor || '',
    date: appt.date || '',
    timeSlot: appt.timeSlot || '',
  });

  // Sync when switching appointments
  useEffect(() => {
    setAssignedDoctor(appt.assignedDoctor || '');
    setConfirmedDate(appt.confirmedDate || appt.date || '');
    setConfirmedTime(appt.confirmedTime || appt.timeSlot || '');
    setAdminNotes(appt.adminNotes || '');
    setStatusNote('');
    setEditingPatient(false);
    setPatientForm({
      name: appt.name || '',
      phone: appt.phone || '',
      age: appt.age != null ? String(appt.age) : '',
      gender: appt.gender || '',
      healthConcern: appt.healthConcern || '',
      service: appt.service || '',
      doctor: appt.doctor || '',
      date: appt.date || '',
      timeSlot: appt.timeSlot || '',
    });
  }, [appt.id, appt.assignedDoctor, appt.confirmedDate, appt.confirmedTime, appt.adminNotes, appt.date, appt.timeSlot, appt.name, appt.phone, appt.age, appt.gender, appt.healthConcern, appt.service, appt.doctor]);

  const saveAdminFields = () => {
    onUpdateFields(appt.id, {
      assignedDoctor: assignedDoctor || null,
      confirmedDate: confirmedDate || null,
      confirmedTime: confirmedTime || null,
      adminNotes: adminNotes || null,
    });
  };

  const savePatientForm = () => {
    const payload = {
      name: patientForm.name?.trim() || null,
      phone: patientForm.phone?.trim() || null,
      age: patientForm.age ? parseInt(patientForm.age, 10) : null,
      gender: patientForm.gender || null,
      healthConcern: patientForm.healthConcern || null,
      service: patientForm.service || null,
      doctor: patientForm.doctor || null,
      date: patientForm.date || null,
      timeSlot: patientForm.timeSlot || null,
    };
    onUpdatePatient(appt.id, payload);
    setEditingPatient(false);
  };

  // Doctors filtered by selected department (in the edit form)
  const editFormDoctors = !patientForm.service
    ? doctors
    : doctors.filter(
        (d) => (d.specialization || '').trim().toLowerCase() === patientForm.service.trim().toLowerCase(),
      );

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3">
          <span>{appt.name}</span>
          <Badge variant="outline" className={`${meta.cls} font-medium`}>{meta.label}</Badge>
        </SheetTitle>
      </SheetHeader>

      <Tabs defaultValue="details" className="mt-6">
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
          <TabsTrigger value="status" data-testid="tab-status">Status &amp; Booking</TabsTrigger>
          <TabsTrigger value="log" data-testid="tab-log">Comm Log</TabsTrigger>
        </TabsList>

        {/* ----- Details tab ----- */}
        <TabsContent value="details" className="space-y-4 mt-4">
          {!editingPatient ? (
            <>
              <Section title="Patient">
                <KV k="Name" v={appt.name} />
                <KV k="Phone / WhatsApp" v={appt.phone} />
                <KV k="Age" v={appt.age || '—'} />
                <KV k="Gender" v={appt.gender || '—'} />
                <KV k="Health concern" v={appt.healthConcern || '—'} pre />
              </Section>

              <Section title="Request">
                <KV k="Department" v={appt.service || '—'} />
                <KV k="Preferred doctor" v={appt.doctor || '—'} />
                <KV k="Preferred date" v={appt.date} />
                <KV k="Preferred time" v={appt.timeSlot} />
                <KV k="Submitted" v={formatDateTime(appt.createdAt)} />
              </Section>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => setEditingPatient(true)}
                data-testid="edit-patient-btn"
              >
                Edit patient details
              </Button>
            </>
          ) : (
            <Section title="Edit patient details">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">Name</Label>
                  <Input
                    value={patientForm.name}
                    onChange={(e) => setPatientForm((f) => ({ ...f, name: e.target.value }))}
                    data-testid="edit-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Phone / WhatsApp</Label>
                  <Input
                    value={patientForm.phone}
                    onChange={(e) => setPatientForm((f) => ({ ...f, phone: e.target.value }))}
                    data-testid="edit-phone-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Age</Label>
                  <Input
                    type="number"
                    min="0"
                    max="120"
                    value={patientForm.age}
                    onChange={(e) => setPatientForm((f) => ({ ...f, age: e.target.value }))}
                    data-testid="edit-age-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Gender</Label>
                  <Select
                    value={patientForm.gender}
                    onValueChange={(v) => setPatientForm((f) => ({ ...f, gender: v }))}
                  >
                    <SelectTrigger data-testid="edit-gender-select"><SelectValue placeholder="Select gender" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                      <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs text-gray-500">Department</Label>
                  <Select
                    value={patientForm.service}
                    onValueChange={(v) => setPatientForm((f) => ({ ...f, service: v, doctor: '' }))}
                  >
                    <SelectTrigger data-testid="edit-service-select"><SelectValue placeholder="Select department" /></SelectTrigger>
                    <SelectContent>
                      {(departments || []).map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs text-gray-500">Preferred doctor</Label>
                  <Select
                    value={patientForm.doctor}
                    onValueChange={(v) => setPatientForm((f) => ({ ...f, doctor: v }))}
                  >
                    <SelectTrigger data-testid="edit-doctor-select"><SelectValue placeholder="Select doctor" /></SelectTrigger>
                    <SelectContent>
                      {editFormDoctors.map((d) => (
                        <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Preferred date</Label>
                  <Input
                    type="date"
                    value={patientForm.date}
                    onChange={(e) => setPatientForm((f) => ({ ...f, date: e.target.value }))}
                    data-testid="edit-date-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Preferred time</Label>
                  <Input
                    value={patientForm.timeSlot}
                    onChange={(e) => setPatientForm((f) => ({ ...f, timeSlot: e.target.value }))}
                    placeholder="10:00 AM - 11:00 AM"
                    data-testid="edit-time-input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs text-gray-500">Health concern</Label>
                  <Textarea
                    rows={3}
                    value={patientForm.healthConcern}
                    onChange={(e) => setPatientForm((f) => ({ ...f, healthConcern: e.target.value }))}
                    data-testid="edit-concern-input"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button
                  className="flex-1 bg-[#14D1A2] hover:bg-[#10B88A]"
                  onClick={savePatientForm}
                  data-testid="save-patient-btn"
                >
                  Save changes
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setEditingPatient(false)}
                  data-testid="cancel-patient-btn"
                >
                  Cancel
                </Button>
              </div>
            </Section>
          )}

          <div className="flex gap-2">
            <Button
              className="flex-1 bg-green-600 hover:bg-green-700"
              onClick={() => onOpenWhatsApp(appt, 'follow_up')}
              data-testid="detail-wa-followup-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp follow-up
            </Button>
            <Button
              className="flex-1 bg-green-700 hover:bg-green-800"
              onClick={() => onOpenWhatsApp(appt, 'confirmation')}
              data-testid="detail-wa-confirm-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp confirmation
            </Button>
          </div>
        </TabsContent>

        {/* ----- Status & Booking tab ----- */}
        <TabsContent value="status" className="space-y-5 mt-4">
          <Section title="Confirmed booking">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-500">Assigned doctor</Label>
                <Select value={assignedDoctor} onValueChange={setAssignedDoctor}>
                  <SelectTrigger data-testid="detail-assigned-doctor"><SelectValue placeholder="Select doctor" /></SelectTrigger>
                  <SelectContent>
                    {doctors.map((d) => (
                      <SelectItem key={d.id} value={d.name}>{d.name} — {d.specialization}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Confirmed date</Label>
                <Input type="date" value={confirmedDate} onChange={(e) => setConfirmedDate(e.target.value)} data-testid="detail-confirmed-date" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs text-gray-500">Confirmed time</Label>
                <Input
                  placeholder="e.g. 11:00 AM - 11:30 AM"
                  value={confirmedTime}
                  onChange={(e) => setConfirmedTime(e.target.value)}
                  data-testid="detail-confirmed-time"
                />
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-xs text-gray-500">Internal notes</Label>
              <Textarea
                rows={3}
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                data-testid="detail-admin-notes"
              />
            </div>
            <Button className="mt-3 bg-[#14D1A2] hover:bg-[#10B88A]" onClick={saveAdminFields} data-testid="detail-save-fields">
              Save booking details
            </Button>
          </Section>

          <Section title="Update status">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {STATUSES.map((s) => (
                <Button
                  key={s.value}
                  size="sm"
                  variant={appt.status === s.value ? 'default' : 'outline'}
                  className={appt.status === s.value ? 'bg-[#14D1A2] hover:bg-[#10B88A]' : ''}
                  onClick={() => onUpdateStatus(appt.id, s.value, statusNote)}
                  data-testid={`status-btn-${s.value}`}
                >
                  {s.label}
                </Button>
              ))}
            </div>
            <Input
              className="mt-3"
              placeholder="Optional note (saved with status change)"
              value={statusNote}
              onChange={(e) => setStatusNote(e.target.value)}
              data-testid="detail-status-note"
            />
          </Section>

          <Section title="Status history">
            {(appt.statusHistory || []).length === 0 ? (
              <p className="text-sm text-gray-500">No history yet.</p>
            ) : (
              <ul className="space-y-2">
                {appt.statusHistory.slice().reverse().map((h, i) => {
                  const m = STATUS_MAP[h.status] || { label: h.status, cls: 'bg-gray-100 text-gray-700 border-gray-200' };
                  return (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      <Badge variant="outline" className={`${m.cls} flex-shrink-0`}>{m.label}</Badge>
                      <div className="flex-1">
                        <div className="text-gray-700">{h.note || '—'}</div>
                        <div className="text-xs text-gray-500">{formatDateTime(h.at)} · {h.by || 'system'}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Section>
        </TabsContent>

        {/* ----- Comm Log tab ----- */}
        <TabsContent value="log" className="space-y-4 mt-4">
          <Section title="Add log entry">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Select value={logKind} onValueChange={setLogKind}>
                <SelectTrigger data-testid="log-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COMM_KINDS.map((k) => (
                    <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                className="sm:col-span-2"
                placeholder="Notes (optional)"
                value={logNote}
                onChange={(e) => setLogNote(e.target.value)}
                data-testid="log-note-input"
              />
            </div>
            <Button
              className="mt-3 bg-[#14D1A2] hover:bg-[#10B88A]"
              onClick={() => {
                onAddCommLog(appt.id, logKind, logNote);
                setLogNote('');
              }}
              data-testid="log-add-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add to log
            </Button>
          </Section>

          <Section title="History">
            {(appt.commLog || []).length === 0 ? (
              <p className="text-sm text-gray-500">No communication logged yet.</p>
            ) : (
              <ul className="space-y-2">
                {appt.commLog.slice().reverse().map((c) => (
                  <li key={c.id} className="flex items-start gap-3 text-sm">
                    <Badge variant="outline" className="bg-slate-100 text-slate-800 border-slate-200 flex-shrink-0">
                      <ClipboardList className="w-3 h-3 mr-1" />
                      {COMM_KIND_LABEL[c.kind] || c.kind}
                    </Badge>
                    <div className="flex-1">
                      <div className="text-gray-700 whitespace-pre-wrap">{c.note || '—'}</div>
                      <div className="text-xs text-gray-500">{formatDateTime(c.at)} · {c.by || 'system'}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </TabsContent>
      </Tabs>
    </>
  );
}

function Section({ title, children }) {
  return (
    <div className="border rounded-lg p-4">
      <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Clock className="w-3.5 h-3.5 text-gray-400" />
        {title}
      </h4>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function KV({ k, v, pre = false }) {
  return (
    <div className="grid grid-cols-3 gap-2 text-sm">
      <span className="text-gray-500">{k}</span>
      <span className={`col-span-2 text-gray-800 ${pre ? 'whitespace-pre-wrap' : ''}`}>{v}</span>
    </div>
  );
}
